import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";
import { observeBreakpoint } from "@bitwarden/web-vault/app/shared/breakpoints";
import { ClickOutsideReason } from "@bitwarden/angular/directives/click-outside.directive";
import { MembershipPlanDirective } from "@bitwarden/web-vault/app/directives/membership-plan.directive";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { ConfirmationEnum } from "@bitwarden/web-vault/app/models/enum/confirmation.enum";
import {
  FREE,
  PlanOption,
  PlanOptions,
} from "@bitwarden/web-vault/app/models/types/subscription.type";
import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { ValidationService } from "@bitwarden/common/abstractions/validation.service";
import { ProfileResponse } from "@bitwarden/common/models/response/profile.response";
import { StripeConfigService } from "@bitwarden/web-vault/app/services/api/stripe-config.service";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import {
  PlanFilterConfig,
  PlanFilterChangeEvent,
  FilterOption,
} from "@bitwarden/web-vault/app/gloss/plugins/plugin-filter/plugin-filter-config.model";
import { DialogFactory } from "@bitwarden/web-vault/app/components/dialog/dialogFactory";
import { HelperTrialRemaining } from "@bitwarden/web-vault/app/shared/utils/helper-trial-remaining";

@Component({
  selector: "app-plans",
  templateUrl: "./plans.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: "tw-h-full",
  },
})
export class PlansComponent extends MembershipPlanDirective implements OnInit {
  private confirmationDialogService = inject(ConfirmationDialogService);
  private dialogFactory = new DialogFactory();
  private trialHelper = new HelperTrialRemaining(); // Helper for trial period calculations
  private cdr = inject(ChangeDetectorRef);

  // Single source of truth for where the layout stacks (mirrors tailwind `tb` screen).
  private static readonly STACK_QUERY = "(max-width: 835px)";
  private isStacked = observeBreakpoint(PlansComponent.STACK_QUERY);

  // filterWrapper spans the trigger + desktop panel only (this component's own host spans the
  // whole plans page), mirroring plugins.component's containment scoping.
  @ViewChild("filterWrapper") private filterWrapper?: ElementRef<HTMLElement>;
  @ViewChild("filterToggleButton") private filterToggleButton?: ElementRef<HTMLElement>;
  @ViewChild("desktopFilterPanel") private desktopFilterPanel?: ElementRef<HTMLElement>;
  @ViewChild("mobileFilterPanel") private mobileFilterPanel?: ElementRef<HTMLElement>;

  constructor() {
    super();
    // Reset whichever filter overlay doesn't belong to the current layout, so it can't
    // get stuck open after a resize crosses the breakpoint.
    effect(
      () => {
        if (this.isStacked()) {
          this.desktopFilterOpen.set(false);
        } else {
          this.showMobileFilter.set(false);
        }
      },
      { allowSignalWrites: true },
    );
  }

  protected isApple: boolean = this.appleConfigService.iOSIAPCapability;
  protected readonly planOptions = PlanOptions;
  protected stripeConfigService = inject(StripeConfigService);
  protected apiService = inject(ApiService);
  private notifyService: ConfirmationDialogService = inject(ConfirmationDialogService);
  private validationService = inject(ValidationService);
  protected profile: ProfileResponse;

  // Filter configuration for the new UI
  protected showMobileFilter = signal(false);

  // Signal (not a plain field) so the breakpoint-reset subscribe() callback below can mark
  // this OnPush component dirty even though it isn't running inside a template event.
  protected desktopFilterOpen = signal(false);

  // Billing cycle selection (annual by default)
  protected selectedBillingCycle = signal<"annual" | "monthly">("annual");

  protected marketOptions = signal<FilterOption[]>([
    { id: "aussie-banks", label: "Aussie Banks", selected: true },
    { id: "aussie-broker", label: "Aussie Broker", selected: false, disabled: true },
    { id: "us-banks", label: "US Banks", selected: false, disabled: true },
  ]);

  protected featuresOptions = signal<FilterOption[]>([
    { id: "basic", label: "Basic", selected: false, disabled: true },
    { id: "investment", label: "Investment", selected: false, disabled: true },
    { id: "pro", label: "Pro", selected: false, disabled: true },
  ]);

  protected filterConfig = computed<PlanFilterConfig>(() => ({
    markets: this.marketOptions(),
    features: this.featuresOptions(),
  }));

  protected getSelectedMarket(): FilterOption | undefined {
    return this.marketOptions().find((m) => m.selected);
  }

  protected getSelectedMarketLabel(): string {
    const selectedMarket = this.getSelectedMarket();
    return selectedMarket ? selectedMarket.label : "";
  }

  // activeSubscription() is null before the subscription loads and after logout (the store's
  // clear()). Null means "no paid plan": not premium, and tier "free" — otherwise
  // isPremiumMember's `!== "free"` below would read a missing subscription as a paid one.
  protected hasPremiumSubscription = computed(() => {
    return this.userStoreService.subscription.activeSubscription()?.isPremium ?? false;
  });
  protected currentSubscriptionTier = computed(() => {
    return this.userStoreService.subscription.activeSubscription()?.tier ?? FREE;
  });
  protected isPremiumMember = computed(() => {
    return this.currentSubscriptionTier() !== FREE;
  });

  // Trial / billing display values (shared logic in HelperTrialRemaining)
  protected trialRemainingDays = computed(() =>
    this.trialHelper.getTrialRemainingDays(this.userStoreService.subscription.activeSubscription()),
  );

  protected isInTrialPeriod = computed(() =>
    this.trialHelper.isInTrialPeriod(this.userStoreService.subscription.activeSubscription()),
  );

  protected nextBillingDate = computed(() =>
    this.trialHelper.getNextBillingDate(this.userStoreService.subscription.activeSubscription()),
  );

  protected formattedBillingDate = computed(() =>
    this.trialHelper.formatBillingDate(this.nextBillingDate()),
  );

  async ngOnInit() {
    this.profile = await this.apiService.getProfile();
  }

  async cancelSubscription() {
    // wrong
    // const currentSubscription = this.stripeConfigService.getSubscription();
    const subscriptionId = this.userStoreService.subscription.activeSubscription()?.id;
    if (subscriptionId) {
      this.appState.startProcess("general", "Cancelling subscription");
      let cancelled = false;
      try {
        await this.stripeConfigService.cancelSubscription(subscriptionId);
        cancelled = true;
        await this.apiService.refreshIdentityToken();
        this.userStoreService.subscription.triggerChanges();
      } catch (e) {
        if (cancelled) {
          // Stripe already cancelled it, but the plan shown comes from the access token, which
          // only a successful refresh updates: say so instead of reporting a failed cancel (#6269).
          this.notifyService.notify(
            NotificationEnum.warning,
            "warning",
            "subscriptionCanceledPlanUpdatePending",
          );
        } else {
          this.validationService.showError(e);
        }
      } finally {
        this.appState.endProcess("general");
      }
    }
  }

  async switchToFreePlan() {
    const activeSub = this.userStoreService.subscription.activeSubscription();
    if (!activeSub) {
      // Nothing to cancel.
      return;
    }
    if (activeSub.paymentProvider === "apple") {
      this.notifyService.notify(NotificationEnum.warning, "warning", "manageSubscriptionInApple");
      return;
    }
    const isConfirmed = await this.confirmationDialogService.confirmFor(
      ConfirmationEnum.switchToFreePlan,
    );
    if (isConfirmed) {
      await this.cancelSubscription();
    }
  }

  protected onFilterChange(event: PlanFilterChangeEvent): void {
    if (event.type === "market") {
      this.handleMarketFilterChange(event);
    } else if (event.type === "features") {
      this.handleFeaturesFilterChange(event);
    }
  }

  private handleMarketFilterChange(event: PlanFilterChangeEvent): void {
    if (event.isAllChange) {
      // Select/deselect all markets (skip disabled ones)
      this.marketOptions.update((options) =>
        options.map((m) => ({
          ...m,
          selected: m.disabled ? m.selected : event.checked!,
        })),
      );
    } else if (event.selectedIds) {
      // Mobile dropdown: update based on selected IDs
      this.marketOptions.update((options) =>
        options.map((m) => ({
          ...m,
          selected: event.selectedIds!.includes(m.id),
        })),
      );
    } else if (event.option) {
      // Desktop: ensure only one market is selected at a time (exclusive selection)
      if (event.checked) {
        this.marketOptions.update((options) =>
          options.map((m) => ({
            ...m,
            selected: m.id === event.option!.id,
          })),
        );
      } else {
        this.marketOptions.update((options) =>
          options.map((m) => (m.id === event.option!.id ? { ...m, selected: false } : m)),
        );
      }
    }
  }

  private handleFeaturesFilterChange(event: PlanFilterChangeEvent): void {
    if (event.isAllChange) {
      // Select/deselect all features (skip disabled ones)
      this.featuresOptions.update((options) =>
        options.map((f) => ({
          ...f,
          selected: f.disabled ? f.selected : event.checked!,
        })),
      );
    } else if (event.selectedIds) {
      // Mobile dropdown: update based on selected IDs
      this.featuresOptions.update((options) =>
        options.map((f) => ({
          ...f,
          selected: event.selectedIds!.includes(f.id),
        })),
      );
    } else if (event.option) {
      // Desktop: toggle individual feature
      this.featuresOptions.update((options) =>
        options.map((f) => (f.id === event.option!.id ? { ...f, selected: event.checked! } : f)),
      );
    }
  }

  protected toggleMobileFilter(): void {
    if (this.showMobileFilter()) {
      this.closeMobileFilter();
    } else {
      this.openMobileFilter();
    }
  }

  /** Keyboard/screen-reader users have no other way to reach the sheet than the trigger. */
  private openMobileFilter(): void {
    this.showMobileFilter.set(true);
    this.cdr.detectChanges();
    this.mobileFilterPanel?.nativeElement.focus({ preventScroll: true });
  }

  /**
   * Centralises every dismissal path (backdrop click, Escape, down-arrow button) so
   * showMobileFilter only ever gets set in one place, guarded on it already being open —
   * ClickOutsideDirective's Escape listener is global, so an Escape meant for something else
   * while this sheet is closed must not steal focus.
   */
  protected closeMobileFilter(restoreFocus = true): void {
    if (!this.showMobileFilter()) {
      return;
    }
    this.showMobileFilter.set(false);
    if (restoreFocus && this.mobileFilterPanel?.nativeElement.contains(document.activeElement)) {
      this.filterToggleButton?.nativeElement.focus();
    }
  }

  protected toggleDesktopFilter(): void {
    if (this.desktopFilterOpen()) {
      this.closeDesktopFilter();
    } else {
      this.openDesktopFilter();
    }
  }

  private openDesktopFilter(): void {
    this.desktopFilterOpen.set(true);
    this.cdr.detectChanges();
    this.desktopFilterPanel?.nativeElement.focus({ preventScroll: true });
  }

  /** Same centralising/guarding rationale as closeMobileFilter, scoped to filterWrapper. */
  protected closeDesktopFilter(restoreFocus = true): void {
    if (!this.desktopFilterOpen()) {
      return;
    }
    this.desktopFilterOpen.set(false);
    if (restoreFocus && this.filterWrapper?.nativeElement.contains(document.activeElement)) {
      this.filterToggleButton?.nativeElement.focus();
    }
  }

  /**
   * The mobile sheet and desktop panel share one trigger and one ClickOutsideDirective instance,
   * so a single handler covers both. Escape is global to the document and must close whichever
   * of the two is actually open (only one can be, per layout) with focus restored; a genuine
   * outside click only needs to close the desktop panel here — the mobile sheet already closes
   * via its own explicit backdrop click, and the browser has already moved focus to whatever was
   * clicked, so it shouldn't be yanked back to the trigger.
   */
  protected onFilterClickOutside(reason: ClickOutsideReason = "outside-click"): void {
    if (reason === "escape") {
      this.closeMobileFilter(true);
    }
    this.closeDesktopFilter(reason === "escape");
  }

  protected toggleFilter(): void {
    // Stacked layout -> bottom sheet; non-stacked -> desktop dropdown panel.
    if (this.isStacked()) {
      this.toggleMobileFilter();
    } else {
      this.toggleDesktopFilter();
    }
  }

  protected navigateToPlugins(): void {
    this.router.navigate(["/plugins"], { queryParams: { tab: "store" } });
  }

  protected navigateToSubscription(): void {
    this.router.navigate(["/settings/subscription"]);
  }

  protected selectBillingCycle(cycle: "annual" | "monthly"): void {
    this.selectedBillingCycle.set(cycle);
  }

  protected handlePlanCardClick(
    plan: any,
    event: Event,
    onDetailsClick: (event: Event) => void,
  ): void {
    const isAlreadySelected =
      plan.subscriptionPaymentCycle === this.paymentService.selectedPlan().subscriptionPaymentCycle;

    if (!isAlreadySelected) {
      this.paymentService.selectPlan(plan.subscriptionPaymentCycle);
    }

    onDetailsClick(event);
  }

  protected openAnnualPlanDialog(): void {
    this.openPlanDialog("annual");
  }

  protected openMonthlyPlanDialog(): void {
    this.openPlanDialog("monthly");
  }

  protected openYourBundleDialog(): void {
    // Determine the user's current plan type based on their subscription
    const currentCycle = this.userStoreService.subscription.activeSubscription()?.paymentCycle;
    const planType: "annual" | "monthly" = currentCycle === "year" ? "annual" : "monthly";

    this.dialogFactory.openPlanDetailsDialog({
      title: "yourBundle",
      planType: planType,
      marketLabel: this.getSelectedMarketLabel(),
      showSwitchButton: false,
      showSubscribeButton: false,
      onAnnualAction: undefined,
      onMonthlyAction: undefined,
      onCancelSubscription: this.switchToFreePlan.bind(this),
    });
  }

  private openPlanDialog(planType: "annual" | "monthly"): void {
    const hasPremium = this.hasPremiumSubscription();

    const showSwitchButton = hasPremium;
    const showSubscribeButton = !hasPremium;

    // Pass both actions and let the dialog decide which to use based on selected cycle
    // For switch actions, pass the selected payment method ('add' = true, 'skip' = false)
    const onAnnualAction = (withCard: boolean) => this.switchToAnnualPlan(withCard);

    const onMonthlyAction = (withCard: boolean) => this.switchToMonthlyPlan(withCard);

    this.dialogFactory.openPlanDetailsDialog({
      planType,
      marketLabel: this.getSelectedMarketLabel(),
      showSwitchButton,
      showSubscribeButton,
      onAnnualAction,
      onMonthlyAction,
    });
  }

  protected isCurrentBundle(plan: PlanOption): boolean {
    if (!this.hasPremiumSubscription()) {
      return false;
    }
    const currentCycle = this.userStoreService.subscription.activeSubscription().paymentCycle;
    return (
      (plan === this.planOptions.premiumAnnual && currentCycle === "year") ||
      (plan === this.planOptions.premiumMonthly && currentCycle === "month")
    );
  }

  protected async switchToAnnualPlan(withCard: boolean = false): Promise<void> {
    this.paymentService.selectPlan(this.planOptions.premiumAnnual.subscriptionPaymentCycle);
    await this.handlePlanSubmit(withCard);
  }

  protected async switchToMonthlyPlan(withCard: boolean = false): Promise<void> {
    this.paymentService.selectPlan(this.planOptions.premiumMonthly.subscriptionPaymentCycle);
    await this.handlePlanSubmit(withCard);
  }
}
